import type { GetAppointmentsByTypeResponse } from '#services/api/insights.ts';

import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChartDataProvider,
  ChartsAxisHighlight,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
} from '@mui/x-charts-pro';
import { useQuery } from '@tanstack/react-query';

import { appointmentsByType } from '#lib/react-query/queries.ts';
import {
  InfoEmphasis,
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '#routes/Home/Widgets/InfoTemplateComponents.tsx';
import {
  CHART_AXIS_STYLES,
  CHART_SERIES_COLORS,
  CHART_X_AXIS_CONFIG,
  WIDGET_CHART_HEIGHT,
} from '#utils/charts.ts';
import { Base } from './Base';
import { EmptyDataOverlay } from './EmptyDataOverlay.tsx';
import { useAppointmentWidget } from './use-appointment-widget.ts';

const widgetTitle = 'Appointments by Type';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Displays trends in the number of appointments over time, grouped by&nbsp;
        <InfoEmphasis>base appointment type</InfoEmphasis>
        &nbsp;(e.g., In-Person, Telehealth). This helps organizations understand their service mix
        and track changes in demand for different appointment formats.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Count of:" indented>
        <InfoList
          items={[
            'Appointments (not deleted, not canceled)',
            'With a valid appointment type',
            'That started within the selected date range',
            'Respecting all applied filters (date, location, provider, etc.)',
            <>
              Grouped by:
              <InfoList
                items={['Appointment Type', 'Total = The sum of all appointment type counts']}
              />
            </>,
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Appointments with valid base appointment type mapping',
            'Valid appointment types: Video, Group Video, Office Video, Phone, Group Phone, In Office, Group In Office',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList
          items={[
            'Deleted appointments',
            'Canceled appointments',
            'Messaging appointment types',
            'Appointments without a valid type mapping (rare)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            'Shows appointment volume only—does not measure confirmation, completion, or no-show rates',
            'Deleted and canceled appointments are excluded from all type counts and from the total',
            'Total count equals the sum of all appointment type counts',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

function select(response: GetAppointmentsByTypeResponse) {
  return {
    ...response,
    data: response.data.map((item) => {
      return {
        ...item,
        period: new Date(item.period),
      };
    }),
  };
}

export function AppointmentsByType() {
  const widget = useAppointmentWidget();

  const theme = useTheme();

  // Fetch scheduled appointments by type data
  const { data, isLoading } = useQuery({
    ...appointmentsByType(widget.getCommonRequestParams()),
    select,
  });

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <Base
      title={widgetTitle}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath()}
    >
      {(isOpenedInDialog) => (
        <Box height="100%">
          {data && data.data.length > 0 ? (
            <ChartDataProvider
              colors={(mode) => CHART_SERIES_COLORS[mode]}
              dataset={data.data}
              height={isOpenedInDialog ? undefined : WIDGET_CHART_HEIGHT}
              margin={{ right: 30 }}
              series={[
                {
                  dataKey: 'scheduledAppointments',
                  label: 'Total',
                  labelMarkType: 'circle',
                  type: 'line',
                },
                {
                  dataKey: 'inPersonAppointments',
                  label: 'In-Person',
                  labelMarkType: 'circle',
                  type: 'line',
                },
                {
                  dataKey: 'telehealthAppointments',
                  label: 'Telehealth',
                  labelMarkType: 'circle',
                  type: 'line',
                },
                {
                  dataKey: 'officeVideoAppointments',
                  label: 'Office Video',
                  labelMarkType: 'circle',
                  type: 'line',
                },
                {
                  dataKey: 'phoneAppointments',
                  label: 'Phone',
                  labelMarkType: 'circle',
                  type: 'line',
                },
              ]}
              xAxis={[
                {
                  dataKey: 'period',
                  height: 60,
                  label: 'Dates',
                  scaleType: 'time',
                  ...CHART_X_AXIS_CONFIG[data.groupedBy],
                },
              ]}
              yAxis={[
                {
                  label: 'Number of Appointments',
                },
              ]}
            >
              <ChartsLegend
                sx={{
                  justifyContent: 'center',
                  '.MuiChartsLegend-label': {
                    color: theme.palette.muted[4],
                  },
                }}
              />
              <ChartsSurface>
                <ChartsXAxis sx={CHART_AXIS_STYLES(theme)} />
                <ChartsYAxis sx={CHART_AXIS_STYLES(theme)} />
                <LinePlot />
                <MarkPlot />
                <ChartsAxisHighlight x="line" y="none" />
                <ChartsTooltip />
              </ChartsSurface>
            </ChartDataProvider>
          ) : (
            <EmptyDataOverlay variant={isOpenedInDialog ? 'dialog' : 'chart'} />
          )}
        </Box>
      )}
    </Base>
  );
}
